import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    ViewChild,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { DeviceService } from '@frontend/vanilla/core';

@Component({
    selector: 'cs-geo-permissions-manager',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    host: {
        'class': 'geo-coordinator permissions-manager',
        '[class]': 'managerClasses()',
        '[class.android]': 'deviceService.isAndroid ? true : null',
        '[class.iOS]': 'deviceService.isiOS ? true : null',
        '[class.chrome]': 'deviceService.isChrome ? true : null',
        '[class.nexus]': 'deviceService.isNexus ? true : null',
        '[class.touch]': 'deviceService.isTouch ? true : null',
        '[class.mobile]': 'deviceService.isMobile ? true : null',
        '[class.mobile-phone]': 'deviceService.isMobilePhone ? true : null',
        '[class.robot]': 'deviceService.isRobot ? true : null',
    },
    templateUrl: './permissions-manager.component.html',
})
export class PermissionsManagerComponent implements AfterViewInit {
    @ViewChild('templateLoaderHost')
    templateLoaderHost: ElementRef<HTMLDivElement> | null = null;

    private readonly matContext = inject(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef<PermissionsManagerComponent>);
    readonly deviceService = inject(DeviceService);

    permissionsInfo = input<{ [key: string]: string }>(this.matContext?.permissionsInfo ?? {});
    permissionsConfig = input<{ [key: string]: string }>(this.matContext?.permissionsConfig ?? {});
    private geoCoordinatorService = this.matContext?.geoCoordinatorService;

    parsedConfig = computed(() => this.prepareConfig(this.permissionsConfig()));
    templateContent = signal<string>('');

    managerClasses = computed(() => {
        const permissionInfoClasses = `permissions-info-status-${this.permissionsInfo()['status'] || ''} permissions-info-reason-${this.permissionsInfo()['reason'] || ''}`;
        const devicePropertyClasses = `orientation-${this.deviceService.currentOrientation} ${this.deviceService.deviceType} os-${this.deviceService.osName} os-version-${this.deviceService.osVersion} vendor-${this.deviceService.vendor}`;
        const browserClasses = `browser-name-${this.deviceService.getCapability('browserName')} browser-version-${this.deviceService.getCapability('browserVersion')}`;

        return `${permissionInfoClasses} ${devicePropertyClasses} ${browserClasses}`;
    });

    async ngAfterViewInit(): Promise<void> {
        if (this.geoCoordinatorService) {
            if (this.geoCoordinatorService?.permissionsManager?.loadAssets) {
                await this.geoCoordinatorService.permissionsManager.loadAssets({
                    configStore: this.permissionsConfig(),
                    hostElement: this.templateLoaderHost?.nativeElement,
                    activateTemplate: true,
                });
            }
        }
    }

    @HostListener('document:closeGeoPermissionsManagerDialog', ['$event'])
    @HostListener('closeGeoPermissionsManagerDialog', ['$event'])
    handleCloseDialog() {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    private prepareConfig(permissionsConfig: { [key: string]: string }) {
        const parsedConfig: { [key: string]: string } = {};
        const permissionDictionaryConfig = permissionsConfig['GeoComply_Hints_Permissions_Dictionary'] ?? '';
        if (permissionDictionaryConfig) {
            const dictionaryEntries = permissionDictionaryConfig.split(';');
            if (dictionaryEntries && dictionaryEntries.length > 0) {
                dictionaryEntries
                    .filter((x) => (x || '').trim())
                    .forEach((dictEntry) => {
                        const [key = '', value = ''] = dictEntry.split('=');
                        parsedConfig[key] = value;
                    });
            }
        }

        return parsedConfig;
    }
}

export enum PermissionsTypes {
    geoPermissions = 'geo-permissions',
    geoCurrentState = 'geo-current-state',
}
